import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import {
  Search, Bookmark, Bell, BellOff, Trash2, ExternalLink,
  Loader2, RefreshCw, AlertCircle, CheckCircle, Clock
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SavedSearches = () => {
  const { formatCurrency } = useCountry();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [searches, setSearches] = useState([]);
  const [checkingId, setCheckingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState(null);

  useEffect(() => {
    fetchSearches();
  }, []);

  const fetchSearches = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_URL}/api/saved-searches/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearches(response.data.searches || []);
    } catch (err) {
      console.error('Failed to fetch saved searches:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        toast({
          title: "Error",
          description: "Failed to load saved searches",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const buildSearchUrl = (criteria) => {
    const params = new URLSearchParams();
    if (criteria.search) params.append('q', criteria.search);
    if (criteria.category) params.append('category', criteria.category);
    if (criteria.location) params.append('location', criteria.location);
    if (criteria.condition) params.append('condition', criteria.condition);
    if (criteria.min_price) params.append('min_price', criteria.min_price);
    if (criteria.max_price) params.append('max_price', criteria.max_price);
    return `/search?${params.toString()}`;
  };

  const handleRunSearch = (search) => {
    navigate(buildSearchUrl(search.criteria));
  };

  const handleCheckNewMatches = async (searchId) => {
    setCheckingId(searchId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/saved-searches/${searchId}/check`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newMatches = response.data.new_matches || 0;
      
      // Update local state
      setSearches(prev => prev.map(s => 
        s.user_id === searchId || s.criteria === searches.find(x => x.user_id === searchId)?.criteria
          ? { ...s, new_matches_count: newMatches, last_checked: new Date().toISOString() }
          : s
      ));

      toast({
        title: newMatches > 0 ? `${newMatches} New Matches!` : "No New Matches",
        description: newMatches > 0 
          ? `Found ${newMatches} new listings matching "${response.data.search_name}"`
          : "No new listings since last check",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to check for new matches",
        variant: "destructive"
      });
    } finally {
      setCheckingId(null);
    }
  };

  const handleToggleNotify = async (search) => {
    try {
      const token = localStorage.getItem('token');
      // Get the search ID from the search object
      const searchId = search.id || search._id;
      
      await axios.put(
        `${API_URL}/api/saved-searches/${searchId}`,
        { notify: !search.notify },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setSearches(prev => prev.map(s => 
        (s.id === searchId || s._id === searchId)
          ? { ...s, notify: !s.notify }
          : s
      ));

      toast({
        title: search.notify ? "Notifications Disabled" : "Notifications Enabled",
        description: search.notify 
          ? "You won't receive alerts for this search"
          : "You'll be notified when new listings match",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update notification settings",
        variant: "destructive"
      });
    }
  };

  const confirmDelete = (search) => {
    setSearchToDelete(search);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!searchToDelete) return;
    
    const searchId = searchToDelete.id || searchToDelete._id;
    setDeletingId(searchId);
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/saved-searches/${searchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSearches(prev => prev.filter(s => s.id !== searchId && s._id !== searchId));
      
      toast({
        title: "Search Deleted",
        description: "Saved search has been removed",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to delete saved search",
        variant: "destructive"
      });
    } finally {
      setDeletingId(null);
      setShowDeleteDialog(false);
      setSearchToDelete(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Bookmark className="h-8 w-8" />
            Saved Searches
          </h1>
          <p className="text-blue-100 mt-2">
            Manage your saved searches and get notified about new listings
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {searches.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-600 mb-2">No Saved Searches</h2>
              <p className="text-gray-500 mb-6">
                Save your search criteria to quickly access them later
              </p>
              <Button 
                onClick={() => navigate('/search')}
                className="bg-gradient-to-r from-blue-600 to-purple-600"
              >
                <Search className="mr-2 h-4 w-4" />
                Start Searching
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {searches.map((search, index) => (
              <Card key={search.id || search._id || index} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    {/* Search Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold">{search.name}</h3>
                        {search.new_matches_count > 0 && (
                          <Badge className="bg-green-500">
                            {search.new_matches_count} new
                          </Badge>
                        )}
                      </div>

                      {/* Criteria Badges */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {search.criteria?.search && (
                          <Badge variant="secondary">
                            <Search className="h-3 w-3 mr-1" />
                            {search.criteria.search}
                          </Badge>
                        )}
                        {search.criteria?.category && (
                          <Badge variant="secondary">{search.criteria.category}</Badge>
                        )}
                        {search.criteria?.location && (
                          <Badge variant="secondary">📍 {search.criteria.location}</Badge>
                        )}
                        {search.criteria?.condition && (
                          <Badge variant="secondary">{search.criteria.condition}</Badge>
                        )}
                        {(search.criteria?.min_price || search.criteria?.max_price) && (
                          <Badge variant="secondary">
                            {formatCurrency(search.criteria.min_price || '0')} - {search.criteria.max_price || '∞'}
                          </Badge>
                        )}
                      </div>

                      {/* Last Checked */}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Last checked: {formatDate(search.last_checked)}
                        </span>
                        <span className="flex items-center gap-1">
                          {search.notify ? (
                            <>
                              <Bell className="h-3 w-3 text-blue-600" />
                              <span className="text-blue-600">Notifications on</span>
                            </>
                          ) : (
                            <>
                              <BellOff className="h-3 w-3" />
                              <span>Notifications off</span>
                            </>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => handleRunSearch(search)}
                        className="bg-gradient-to-r from-blue-600 to-purple-600"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Run Search
                      </Button>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCheckNewMatches(search.id || search._id)}
                          disabled={checkingId === (search.id || search._id)}
                        >
                          {checkingId === (search.id || search._id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleNotify(search)}
                        >
                          {search.notify ? (
                            <Bell className="h-4 w-4 text-blue-600" />
                          ) : (
                            <BellOff className="h-4 w-4" />
                          )}
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => confirmDelete(search)}
                          disabled={deletingId === (search.id || search._id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {deletingId === (search.id || search._id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Saved Search?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{searchToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SavedSearches;
